import {
  MedicalCasePhaseDocument,
  MedicalCasePhaseDocumentState,
  MedicalCasePhaseDocumentVersion,
  MedicalCasePhases,
  MedicalCaseProcessingPhase,
} from '../../../domain/medical-case/model';
import { StepDocumentViewEntry } from '../model/pipeline-step.model';

export const VISIBLE_PHASE_ORDER: readonly MedicalCaseProcessingPhase[] = [
  'MEDICAL_RECORD_INIT',
  'MEDICAL_RECORD_DIGITIZATION',
  'MEDICAL_RECORD_TRANSLATION',
  'MEDICAL_RECORD_ANONYMIZATION',
  'MEDICAL_RECORD_SUMMARY',
  'MEDICAL_RECORD_PACKAGING',
];

export const ALL_PHASE_ORDER: readonly MedicalCaseProcessingPhase[] = [
  'MEDICAL_RECORD_INIT',
  'MEDICAL_RECORD_CONVERSION',
  'MEDICAL_RECORD_DIGITIZATION',
  'MEDICAL_RECORD_TRANSLATION',
  'MEDICAL_RECORD_MERGE',
  'MEDICAL_RECORD_ANONYMIZATION',
  'MEDICAL_RECORD_ANONYMIZED_SUMMARY',
  'MEDICAL_RECORD_SUMMARY',
  'MEDICAL_RECORD_DOSSIER',
  'MEDICAL_RECORD_PACKAGING',
];

/**
 * @Applied: [PR1] - Canonical document/phase resolver for medical case steps.
 * Centralizes originalDocumentId resolution, cross-phase document lookup, and version identification.
 */
export class MedicalCaseDocumentResolver {
  /**
   * Resolves the canonical originalDocumentId for any given document representation.
   * Priority: originalDocumentId > id.
   */
  static resolveOriginalId(
    document: MedicalCasePhaseDocument | null | undefined
  ): string | null {
    const id = document?.originalDocumentId?.trim() || document?.id?.trim();
    return id || null;
  }

  /**
   * Finds a specific document in a given phase by its canonical originalId.
   */
  static getDocumentInPhase(
    phases: MedicalCasePhases | null | undefined,
    phase: MedicalCaseProcessingPhase,
    originalId: string | null | undefined
  ): MedicalCasePhaseDocument | null {
    const normalizedId = originalId?.trim();
    if (!phases || !normalizedId) {
      return null;
    }

    const documents = phases[phase]?.documents ?? [];
    return (
      documents.find(
        doc => this.resolveOriginalId(doc) === normalizedId
      ) ?? null
    );
  }

  /**
   * Finds the latest version of a document in the most advanced visible phase reached.
   */
  static getLatestVisiblePhaseDocument(
    phases: MedicalCasePhases | null | undefined,
    originalId: string | null | undefined,
    upToPhase: MedicalCaseProcessingPhase = 'MEDICAL_RECORD_PACKAGING',
    phaseOrder: readonly MedicalCaseProcessingPhase[] = VISIBLE_PHASE_ORDER,
  ): { phase: MedicalCaseProcessingPhase; document: MedicalCasePhaseDocument } | null {
    const normalizedId = originalId?.trim();
    if (!phases || !normalizedId) {
      return null;
    }

    const upToIndex = phaseOrder.indexOf(upToPhase);
    const searchLimit = upToIndex === -1 ? phaseOrder.length - 1 : upToIndex;

    for (let i = searchLimit; i >= 0; i--) {
      const phase = phaseOrder[i];
      const doc = this.getDocumentInPhase(phases, phase, normalizedId);
      if (doc) {
        return { phase, document: doc };
      }
    }

    return null;
  }

  /**
   * Builds a StepDocumentViewEntry for a specific document and phase.
   * Centralizes the fallback logic: if a document hasn't reached the current phase,
   * it shows data from the latest reached visible phase and marks it inactive.
   */
  static buildViewEntry(
    phases: MedicalCasePhases | null | undefined,
    currentPhase: MedicalCaseProcessingPhase,
    originalId: string,
    phaseOrder: readonly MedicalCaseProcessingPhase[] = VISIBLE_PHASE_ORDER,
  ): StepDocumentViewEntry | null {
    const normalizedId = originalId.trim();
    if (!normalizedId) return null;

    const phaseDoc = this.getDocumentInPhase(phases, currentPhase, normalizedId);
    const latestVisible = this.getLatestVisiblePhaseDocument(phases, normalizedId, currentPhase, phaseOrder);

    if (!latestVisible) return null;

    const displayDoc = phaseDoc || latestVisible.document;
    const activeVersion = displayDoc.activeVersion;

    return {
      originalDocumentId: normalizedId,
      isActive: phaseDoc != null,
      lastAvailablePhase: latestVisible.phase,
      activeVersionId: activeVersion?.id?.trim() ?? null,
      activeVersionFileId: activeVersion?.fileId?.trim() ?? null,
      phaseState: activeVersion?.state ?? null,
    };
  }

  /**
   * Returns all canonical originalIds present in the INIT phase.
   */
  static getAllOriginalIds(
    phases: MedicalCasePhases | null | undefined
  ): string[] {
    const initPhase = phases?.MEDICAL_RECORD_INIT ?? phases?.SECOND_OPINION_INIT;
    return (initPhase?.documents ?? [])
      .map(doc => this.resolveOriginalId(doc))
      .filter((id): id is string => !!id);
  }

  /**
   * Resolves an originalId from a fileId by searching through all phases.
   * Useful when we only have a fileId (e.g. from an error or a simple link).
   */
  static resolveOriginalIdFromFileId(
    phases: MedicalCasePhases | null | undefined,
    fileId: string | null | undefined
  ): string | null {
    const normalizedFileId = fileId?.trim();
    if (!phases || !normalizedFileId) {
      return null;
    }

    // Search INIT first as it's the source of truth for original files
    const initPhase = phases.MEDICAL_RECORD_INIT ?? phases.SECOND_OPINION_INIT;
    const initMatch = (initPhase?.documents ?? []).find(doc =>
      doc.originalDocument?.fileId === normalizedFileId || 
      doc.activeVersion?.fileId === normalizedFileId
    );
    if (initMatch) return this.resolveOriginalId(initMatch);

    // Search all other phases
    for (const phaseData of Object.values(phases)) {
      const match = (phaseData?.documents ?? []).find(doc =>
        doc.originalDocument?.fileId === normalizedFileId ||
        doc.activeVersion?.fileId === normalizedFileId
      );
      if (match) return this.resolveOriginalId(match);
    }

    return null;
  }

  /**
   * Resolves a document in the given phase, with first-doc fallback when no originalDocumentId is provided.
   */
  static resolvePhaseDocument(
    phases: MedicalCasePhases | null | undefined,
    phase: MedicalCaseProcessingPhase,
    originalDocumentId?: string | null
  ): MedicalCasePhaseDocument | null {
    if (phases == null) {
      return null;
    }

    const normalizedOriginalDocumentId = originalDocumentId?.trim();
    if (normalizedOriginalDocumentId) {
      return this.getDocumentInPhase(phases, phase, normalizedOriginalDocumentId);
    }

    return phases[phase]?.documents?.[0] ?? null;
  }

  /**
   * Gets the active version for a document identified by originalId in a given phase.
   */
  static getActiveVersionForOriginalDocument(
    phases: MedicalCasePhases | null | undefined,
    phase: MedicalCaseProcessingPhase,
    originalDocumentId: string | null | undefined
  ): MedicalCasePhaseDocumentVersion | null {
    const normalizedOriginalDocumentId = originalDocumentId?.trim();
    if (phases == null || !normalizedOriginalDocumentId) {
      return null;
    }

    return (
      this.getDocumentInPhase(phases, phase, normalizedOriginalDocumentId)
        ?.activeVersion ?? null
    );
  }

  /**
   * Gets the phase state for a document identified by originalId.
   */
  static getPhaseStateForOriginalDocument(
    phases: MedicalCasePhases | null | undefined,
    phase: MedicalCaseProcessingPhase,
    originalDocumentId: string | null | undefined
  ): MedicalCasePhaseDocumentState | null {
    return (
      this.getActiveVersionForOriginalDocument(phases, phase, originalDocumentId)
        ?.state ?? null
    );
  }

  /**
   * Gets the phase state for a document identified by original fileId.
   */
  static getPhaseStateForOriginalFile(
    phases: MedicalCasePhases | null | undefined,
    phase: MedicalCaseProcessingPhase,
    originalFileId: string | null | undefined
  ): MedicalCasePhaseDocumentState | null {
    const originalDocumentId = this.resolveOriginalIdFromFileId(phases, originalFileId);
    return this.getPhaseStateForOriginalDocument(phases, phase, originalDocumentId);
  }

  /**
   * Checks whether the version for a document identified by originalId is outdated.
   */
  static isPhaseOutdatedForOriginalDocument(
    phases: MedicalCasePhases | null | undefined,
    phase: MedicalCaseProcessingPhase,
    originalDocumentId: string | null | undefined
  ): boolean {
    return (
      this.getActiveVersionForOriginalDocument(phases, phase, originalDocumentId)
        ?.outdated === true
    );
  }

  /**
   * Checks whether the version for a document identified by original fileId is outdated.
   */
  static isPhaseOutdatedForOriginalFile(
    phases: MedicalCasePhases | null | undefined,
    phase: MedicalCaseProcessingPhase,
    originalFileId: string | null | undefined
  ): boolean {
    const originalDocumentId = this.resolveOriginalIdFromFileId(phases, originalFileId);
    return this.isPhaseOutdatedForOriginalDocument(phases, phase, originalDocumentId);
  }
}
